import React, { useCallback } from 'react';
import { Flex, Heading, Icon, Image, Text, Button } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import {
  MdCalendarMonth,
  MdChevronLeft,
  MdChevronRight,
  MdLocationOn,
  MdPerson2,
  MdPerson3,
} from 'react-icons/md';
import { AiOutlineHome } from 'react-icons/ai';
import { useUser } from '../hooks/useUser';
import { Group } from '../models/group';
import { useScheduleList } from '../hooks/useScheduleList';
import ScheduleList from './ScheduleList';
import { useAuthContext } from '../AuthProvider';
import Header from './Header';

interface GroupInfoProps {
  group: Group;
  participate: (userId: string) => void;
  cancel: (userId: string) => void;
}

const GroupInfo = ({ group, participate, cancel }: GroupInfoProps) => {
  const owner = useUser(group.owner);
  const { upcomingSchedules, pastSchedules } = useScheduleList(group.id);
  const { user } = useAuthContext();
  const isMember = group.members.includes(user?.uid ?? '');

  const handleClick = useCallback(() => {
    if (isMember) {
      cancel(user?.uid!);
      return;
    }
    participate(user?.uid!);
  }, [isMember, participate, cancel, user]);

  return (
    <Flex flexDir="column" width="100%" px="1rem">
      <Header
        title={group.name}
        leftIcon={MdChevronLeft}
        leftTo={-1}
        rightIcon={AiOutlineHome}
        rightTo="/"
      />
      <Flex pos="relative" h="100%" flexDirection="column" align="center">
        {group.thumbnail && (
          <Image
            src={group.thumbnail}
            alt="thumbnail"
            objectFit="contain"
            maxH="220px"
          />
        )}
        <Flex
          flexDir="column"
          alignItems="start"
          width="100%"
          pb="12px"
          borderBottom="1px solid #E8E8E8"
        >
          <Heading
            as="h2"
            size="lg"
            lineHeight="tall"
            textAlign="center"
            noOfLines={1}
          >
            {group.name}
          </Heading>
          <Flex alignItems="center">
            <Icon
              borderRadius="50%"
              as={MdPerson3}
              background="#D84E46"
              color="white"
            />
            <Text ml="0.5rem" as="b">
              {owner?.name}
            </Text>
          </Flex>
        </Flex>
        <Flex justify="start" width="100%" mt="12px">
          <Flex
            py="7px"
            px="12px"
            alignItems="center"
            color="#8B8B8B"
            borderRadius="4px"
            bgColor="#F0F0F0"
            mr="4px"
          >
            <Icon as={MdLocationOn} />
            <Text fontWeight="semibold" fontSize="12px">
              {group.location}
            </Text>
          </Flex>
          <Flex
            py="7px"
            px="12px"
            alignItems="center"
            color="#8B8B8B"
            borderRadius="4px"
            bgColor="#F0F0F0"
            mr="4px"
          >
            <Icon as={MdPerson2} />
            <Text fontWeight="semibold" fontSize="12px">
              {group.members.length}/{group.total}
            </Text>
          </Flex>
          <Flex
            py="7px"
            px="12px"
            alignItems="center"
            color="#8B8B8B"
            borderRadius="4px"
            bgColor="#F0F0F0"
          >
            <Icon as={MdCalendarMonth} />
            <Text fontWeight="semibold" fontSize="12px">
              {upcomingSchedules.length + pastSchedules.length}개의 일정
            </Text>
          </Flex>
        </Flex>
        <Flex
          flexDir="column"
          width="100%"
          py="3rem"
          borderBottom="1px solid #E8E8E8"
        >
          <Text as="b" fontSize="1.5rem" mb="14px">
            모임 소개
          </Text>
          <Text>{group.description}</Text>
        </Flex>
        <Link style={{ width: '100%' }} to={`/group/${group.id}/boardgames`}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            py="24px"
            borderBottom="1px solid #E8E8E8"
          >
            <Text as="b" fontSize="18px">
              보유한 보드게임
            </Text>
            <Text fontSize="14px" color="#858585" ml="14px" mr="auto">
              {group.boardGames.length || 0}개
            </Text>
            <Icon as={MdChevronRight} boxSize="24px" color="black" />
          </Flex>
        </Link>
        <Link style={{ width: '100%' }} to={`/group/${group.id}/members`}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            py="24px"
            borderBottom="1px solid #E8E8E8"
          >
            <Text as="b" fontSize="18px">
              모임 멤버
            </Text>
            <Text fontSize="14px" color="#858585" ml="14px" mr="auto">
              {group.members.length || 0}명
            </Text>
            <Icon as={MdChevronRight} boxSize="24px" color="black" />
          </Flex>
        </Link>
        <ScheduleList
          groupId={group.id}
          isMember={isMember}
          upcomingSchedules={upcomingSchedules}
          pastSchedules={pastSchedules}
        />
      </Flex>
      <Flex borderTop="1px solid #D9D9D9" pt="12px" pb="34px" px="1rem">
        <Button
          onClick={handleClick}
          width="100%"
          py="14px"
          borderRadius="40px"
          color={isMember ? '#9E9E9E' : 'white'}
          bgColor={isMember ? '#F0F0F0' : '#D84E46'}
          _active={{
            bgColor: isMember ? '#FFE5E4' : '#AB170F',
          }}
          _hover={{
            bgColor: isMember ? '#FFE5E4' : '#AB170F',
          }}
        >
          모임 가입하기
        </Button>
      </Flex>
    </Flex>
  );
};

export default GroupInfo;
